import { Alert, Card, Col, ConfigProvider, DatePicker, Divider, Form, Row, Statistic, Typography, Checkbox } from "antd";
import { rightsService } from "../../../../services/rightsService";

import SDIModal from "../../../../components/ui/SDIModal";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";
import AvenantAgencySelector from "./AvenantAgencySelector";
import { applyAvenantAgenceCommission } from "../../../../services/avenantCommissionService";

// Habilitation autorisant à suspendre un contrat À MOINS DE 3 MOIS de son échéance.
// ⚠️ UNIQUE endroit à modifier si le code change — la ligne doit exister dans p_habilitation,
// sinon personne n'est habilité et la règle des 3 mois reste bloquante pour tous.
const HABILITATION_SUSPENSION_HORS_DELAI = "HCO098";

// Durée restante minimale (en mois) exigée pour une suspension « normale ».
const DUREE_RESTANTE_MIN_MOIS = 3;

const { Text, Title } = Typography;

const SuspensionContrat = (props) => {
    const [modalSuspension, setModalSuspension] = useState(props.open);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // États pour les données du contrat
    const [contratActuel, setContratActuel] = useState(null);
    const [dateSuspension, setDateSuspension] = useState(dayjs());
    const [dateFinSuspension, setDateFinSuspension] = useState(null);
    const [dureeRestante, setDureeRestante] = useState(0);
    const [attestationsRendues, setAttestationsRendues] = useState(false);
    // Agence ciblée + taux (siège) à appliquer au contrat résultant de l'avenant
    const [avCom, setAvCom] = useState({ agence: null, taux: null, intermediaire: null });

    useEffect(() => {
        setModalSuspension(props.open);
        if (props.open && props?.contrat?.codeId) {
            console.log("Contrat à suspendre:", props?.contrat);
            setContratActuel(props?.contrat);

            // Durée restante ÉVALUÉE À LA DATE DE SUSPENSION choisie (par défaut aujourd'hui),
            // et non à la date du jour : une suspension antidatée doit être jugée à sa vraie date.
            const dateEcheance = dayjs(props?.contrat?.dateEcheance);
            const dateEffetContrat = props?.contrat?.dateEffet ? dayjs(props.contrat.dateEffet) : null;

            // Date de suspension par défaut = aujourd'hui, RAMENÉE dans la période [effet, échéance].
            // Pour un contrat ÉCHU, aujourd'hui est hors période (après l'échéance) → on part de
            // l'échéance (l'utilisateur ajuste ensuite à la vraie date, avant l'échéance).
            let defautSuspension = dayjs();
            if (dateEcheance.isValid() && defautSuspension.isAfter(dateEcheance, 'day')) {
                defautSuspension = dateEcheance;
            }
            if (dateEffetContrat && defautSuspension.isBefore(dateEffetContrat, 'day')) {
                defautSuspension = dateEffetContrat;
            }
            setDateSuspension(defautSuspension);
            form.setFieldsValue({ dateSuspension: defautSuspension });

            const duree = dateEcheance.diff(defautSuspension, 'month', true);
            setDureeRestante(duree);

            // Définir la date de fin de suspension max (échéance du contrat)
            setDateFinSuspension(dateEcheance);
        }
    }, [props.open, props?.contrat?.codeId]);

    const { PauseCircleOutlined, CalendarOutlined, WarningOutlined, FileTextOutlined, CheckCircleOutlined } = require('@ant-design/icons');

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
    };

    const handleDateSuspensionChange = (date) => {
        setDateSuspension(date);

        // Recalculer la durée restante depuis la nouvelle date de suspension
        if (contratActuel?.dateEcheance) {
            const dateEcheance = dayjs(contratActuel.dateEcheance);
            const duree = dateEcheance.diff(date, 'month', true);
            setDureeRestante(duree);
        }
    };

    const navigate = useNavigate();

    const enterLoading = () => setLoading(true);
    const outLoading = () => setLoading(false);

    const handleSuspensionSubmit = async () => {
        try {
            await form.validateFields();

            // Règle des 3 mois : évaluée à la date de suspension choisie, et CONTOURNABLE
            // par habilitation (une suspension réelle antidatée peut légitimement tomber
            // à moins de 3 mois de l'échéance).
            if (dureeRestante < DUREE_RESTANTE_MIN_MOIS && !peutForcerHorsDelai) {
                toast.error("La durée restante doit être d'au moins 3 mois pour pouvoir suspendre le contrat");
                return;
            }

            if (!attestationsRendues) {
                toast.error("Vous devez confirmer que les attestations et vignettes ont été récupérées");
                return;
            }

            enterLoading();

            const payload = {
                contratCodeId: props?.contrat?.codeId,
                dateEffet: dateSuspension.format('YYYY-MM-DD'),
                dateFinSuspensionMax: dateFinSuspension.format('YYYY-MM-DD'),
                dureeRestante: Math.floor(dureeRestante),
                attestationsRendues,
                motifSuspension: "Suspension temporaire à la demande de l'assuré"
            };

            console.log("Payload suspension:", payload);

            const response = await axiosInstance.put(
                `/api/contrat/suspension`,
                payload
            );

            await applyAvenantAgenceCommission(response.data?.codeId, avCom.agence, avCom.taux, avCom.intermediaire);
            toast.success("Suspension du contrat effectuée avec succès!");
            setModalSuspension(false);
            props.update(false);
            navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
            window.location.reload();

        } catch (error) {
            console.error(error);
            toast.error("Erreur lors de la suspension du contrat");
        } finally {
            outLoading();
        }
    };

    const handleCancel = () => {
        setModalSuspension(false);
        props.update(false);
    };

    // Contrat échu (échéance déjà passée) ?
    const estEchu = contratActuel?.dateEcheance
        ? dayjs(contratActuel.dateEcheance).isBefore(dayjs(), 'day')
        : false;
    // Habilité à passer outre la règle des 3 mois ? HCO098 (hors délai) pour un contrat en cours ;
    // pour un contrat ÉCHU, HCO081 (antidatage) suffit — la durée restante y est forcément < 3 mois.
    const peutForcerHorsDelai = rightsService(HABILITATION_SUSPENSION_HORS_DELAI)
        || (estEchu && rightsService("HCO081"));

    // La durée restante est-elle suffisante À LA DATE DE SUSPENSION CHOISIE ?
    const dureeSuffisante = dureeRestante >= DUREE_RESTANTE_MIN_MOIS;

    // Le contrat est éligible si la durée suffit OU si l'utilisateur est habilité à forcer.
    const isContratEligible = () => {
        return (dureeSuffisante || peutForcerHorsDelai) &&
            contratActuel?.statut !== 'SINISTRE' &&
            contratActuel?.statut !== 'SUSPENDU';
    };

    const getDateLimiteRemiseEnVigueur = () => {
        return dateSuspension.add(1, 'year');
    };

    return (
        <SDIModal
            title="SUSPENSION DE CONTRAT"
            centered
            confirmLoading={loading}
            open={modalSuspension}
            onOk={handleSuspensionSubmit}
            onCancel={handleCancel}
            width={900}
            zIndex={1940}
            okText="Suspendre le contrat"
            cancelText="Annuler"
        >
            <ConfigProvider locale={frFR}>
                <div className="suspension-content">
                    <AvenantAgencySelector
                        organismeCode={props?.contrat?.organisme?.code}
                        defaultAgence={props?.contrat?.courtier || props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
                        defaultIntermediaire={props?.contrat?.intermediaire}
                        categorie={props?.contrat?.engins?.[0]?.categorie?.codeCategorie}
                        onChange={(ag, t, inter) => setAvCom({ agence: ag, taux: t, intermediaire: inter })}
                    />

                    {/* Informations sur le contrat */}
                    {contratActuel && (
                        <Card className="mb-4" size="small" title="Informations du contrat">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Statistic
                                        title="Numéro de police"
                                        value={contratActuel.numeroPoliceP || contratActuel.numeroPoliceM}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Date d'échéance"
                                        value={dayjs(contratActuel.dateEcheance).format('DD/MM/YYYY')}
                                        prefix={<CalendarOutlined />}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Durée restante"
                                        value={`${Math.floor(dureeRestante)} mois`}
                                        valueStyle={{ color: dureeSuffisante ? '#3f8600' : (peutForcerHorsDelai ? '#d46b08' : '#cf1322') }}
                                        prefix={<CalendarOutlined />}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    )}

                    {/* Conditions d'éligibilité — évaluées à la DATE DE SUSPENSION choisie. */}
                    {!isContratEligible() ? (
                        <Alert
                            message="Contrat non éligible à la suspension"
                            description={
                                contratActuel?.suspendu || contratActuel?.avenant?.code === "SP"
                                    ? "Ce contrat est déjà suspendu. Un contrat suspendu ne peut pas être suspendu à nouveau."
                                    : !dureeSuffisante
                                        ? `À la date de suspension choisie, la durée restante (${Math.floor(dureeRestante)} mois) est inférieure à ${DUREE_RESTANTE_MIN_MOIS} mois. Une habilitation est requise pour passer outre.`
                                        : "Le contrat ne peut pas être suspendu s'il y a eu un sinistre."
                            }
                            type="error"
                            icon={<WarningOutlined />}
                            showIcon
                            className="mb-4"
                        />
                    ) : !dureeSuffisante ? (
                        /* Hors délai, MAIS l'utilisateur est habilité : on l'avertit sans le bloquer. */
                        <Alert
                            message="Suspension hors délai — autorisée par votre habilitation"
                            description={`À la date choisie, il reste ${Math.floor(dureeRestante)} mois avant l'échéance (minimum requis : ${DUREE_RESTANTE_MIN_MOIS} mois). Vous pouvez néanmoins procéder au titre de votre habilitation. Cette opération est historisée.`}
                            type="warning"
                            icon={<WarningOutlined />}
                            showIcon
                            className="mb-4"
                        />
                    ) : (
                        <Alert
                            message="Contrat éligible à la suspension"
                            description="Ce contrat remplit les conditions pour être suspendu temporairement."
                            type="success"
                            icon={<CheckCircleOutlined />}
                            showIcon
                            className="mb-4"
                        />
                    )}

                    {/* Informations importantes */}
                    {/* <Card className="mb-4" size="small" title="Informations importantes sur la suspension">
                        <div className="info-section">
                            <div className="info-item">
                                <WarningOutlined className="info-icon warning" />
                                <Text>
                                    <Text strong>Couverture suspendue :</Text> Pendant la suspension,
                                    aucune garantie d'assurance n'est active.
                                </Text>
                            </div>
                            <div className="info-item">
                                <CalendarOutlined className="info-icon info" />
                                <Text>
                                    <Text strong>Délai de remise en vigueur :</Text> Maximum 1 an à partir
                                    de la date de suspension, sinon perte des droits.
                                </Text>
                            </div>
                            <div className="info-item">
                                <FileTextOutlined className="info-icon success" />
                                <Text>
                                    <Text strong>Aucune prime :</Text> Aucune prime d'assurance ni
                                    ristourne n'est générée pendant la suspension.
                                </Text>
                            </div>
                        </div>
                    </Card> */}

                    <Form form={form} layout="vertical">
                        {/* Date de suspension */}
                        <Form.Item
                            label="Date de début de suspension"
                            name="dateSuspension"
                            rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                            initialValue={dateSuspension}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                value={dateSuspension}
                                onChange={handleDateSuspensionChange}
                                disabledDate={(current) => {
                                    if (!current) return false;
                                    // La suspension est souvent constatée APRÈS COUP : on doit pouvoir
                                    // saisir sa VRAIE date, donc une date passée. On ne borne donc plus
                                    // à « aujourd'hui », mais à la vie du contrat :
                                    //   - pas avant sa date d'effet (rien à suspendre avant),
                                    //   - pas après son échéance (le contrat est déjà terminé).
                                    // La règle des 3 mois n'est PLUS une contrainte du sélecteur : elle
                                    // est évaluée sur la date choisie et contournable par habilitation.
                                    const dateMin = contratActuel?.dateEffet
                                        ? dayjs(contratActuel.dateEffet).startOf('day')
                                        : null;
                                    const dateMax = contratActuel?.dateEcheance
                                        ? dayjs(contratActuel.dateEcheance).endOf('day')
                                        : null;
                                    if (dateMin && current.isBefore(dateMin, 'day')) return true;
                                    if (dateMax && current.isAfter(dateMax, 'day')) return true;
                                    return false;
                                }}
                            />
                        </Form.Item>

                        {/* Informations calculées */}
                        {dateSuspension && (
                            <Card className="mb-4" size="small" title="Informations calculées">
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Statistic
                                            title="Date limite de remise en vigueur"
                                            value={getDateLimiteRemiseEnVigueur().format('DD/MM/YYYY')}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title="Date de fin max (échéance)"
                                            value={dateFinSuspension ? dateFinSuspension.format('DD/MM/YYYY') : 'N/A'}
                                            valueStyle={{ color: '#722ed1' }}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title="Coût de la suspension"
                                            value="0 XAF"
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        {/* Conditions et confirmations */}
                        <Card className="mb-4" size="small" title="Vérifications obligatoires du producteur">
                            <Form.Item
                                name="attestationsRendues"
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            value ? Promise.resolve() : Promise.reject(new Error('Vous devez confirmer la récupération'))
                                    }
                                ]}
                            >
                                <Checkbox
                                    checked={attestationsRendues}
                                    onChange={(e) => setAttestationsRendues(e.target.checked)}
                                >
                                    <Text strong>
                                        Attestations et vignettes physiques récupérées de l'assuré
                                    </Text>
                                </Checkbox>
                            </Form.Item>
                        </Card>

                        {/* Récapitulatif */}
                        <Card className="cost-summary" size="small">
                            <Title level={5}>
                                <PauseCircleOutlined className="mr-2" />
                                Récapitulatif de la suspension
                            </Title>
                            <div className="cost-breakdown">
                                <Row justify="space-between" className="cost-line">
                                    <Col>
                                        <Text>Date de début de suspension:</Text>
                                    </Col>
                                    <Col>
                                        <Text strong>{dateSuspension.format('DD/MM/YYYY')}</Text>
                                    </Col>
                                </Row>

                                <Row justify="space-between" className="cost-line">
                                    <Col>
                                        <Text>Date limite de remise en vigueur:</Text>
                                    </Col>
                                    <Col>
                                        <Text strong className="text-orange-600">
                                            {getDateLimiteRemiseEnVigueur().format('DD/MM/YYYY')}
                                        </Text>
                                    </Col>
                                </Row>

                                <Row justify="space-between" className="cost-line">
                                    <Col>
                                        <Text>Durée restante après suspension:</Text>
                                    </Col>
                                    <Col>
                                        <Text>{Math.floor(dureeRestante)} mois</Text>
                                    </Col>
                                </Row>

                                <Divider className="my-2" />

                                <Row justify="space-between" className="cost-line total-cost">
                                    <Col>
                                        <Text strong className="text-lg">COÛT TOTAL:</Text>
                                    </Col>
                                    <Col>
                                        <Text strong className="text-lg text-green-600">
                                            {formatCurrency(0)}
                                        </Text>
                                    </Col>
                                </Row>
                            </div>
                        </Card>
                    </Form>
                </div>
            </ConfigProvider>

            <style jsx>
                {`
                    .suspension-content {
                        max-height: 70vh;
                        overflow-y: auto;
                    }
                    
                    .info-section {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    
                    .info-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                    }
                    
                    .info-icon {
                        margin-top: 2px;
                        font-size: 16px;
                    }
                    
                    .info-icon.warning {
                        color: #faad14;
                    }
                    
                    .info-icon.info {
                        color: #1890ff;
                    }
                    
                    .info-icon.success {
                        color: #52c41a;
                    }
                    
                    .cost-summary {
                        background-color: #f8f9fa;
                        border: 1px solid #e9ecef;
                    }
                    
                    .cost-breakdown .cost-line {
                        margin-bottom: 8px;
                    }
                    
                    .cost-breakdown .total-cost {
                        background-color: #f6ffed;
                        padding: 8px;
                        border-radius: 4px;
                        margin-top: 8px;
                    }
                    
                    .mr-2 { margin-right: 8px; }
                    .mb-4 { margin-bottom: 16px; }
                    .my-2 { margin: 8px 0; }
                    .text-lg { font-size: 16px; }
                    .text-green-600 { color: #52c41a; }
                    .text-orange-600 { color: #fa8c16; }
                `}
            </style>
        </SDIModal>
    );
};

SuspensionContrat.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default SuspensionContrat;
